import React, { useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../services/api';
import { 
    LayoutDashboard, 
    PieChart, 
    Handshake, 
    LogOut,
    User,
    ArrowDownLeft,
    ArrowUpRight,
    Shield,
    Newspaper,
    Camera,
    Settings,
    Zap,
    Bot
} from 'lucide-react';

const LOGO_URL = "/logo.jpg";

export const Sidebar = ({ isOpen, onClose }) => {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const fileInputRef = useRef(null);

    const handleLogout = () => {
        logout();
        navigate('/login');
        onClose?.();
    };

    const handleNavClick = () => {
        onClose?.();
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }

        setUploadingAvatar(true);
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const response = await api.post('/profile/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.avatar_url) {
                updateUser({ ...user, avatar_url: response.data.avatar_url });
                toast.success('Profile picture updated!');
            }
        } catch (error) {
            toast.error('Failed to upload profile picture');
        } finally {
            setUploadingAvatar(false);
        }
    };

    const navItems = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/portfolio', icon: PieChart, label: 'Portfolio' },
        { to: '/trading-bot', icon: Bot, label: 'Trading Bot' },
        { to: '/partners', icon: Handshake, label: 'Partners' },
        { to: '/news', icon: Newspaper, label: 'News' },
        { to: '/deposit', icon: ArrowDownLeft, label: 'Deposit' },
        { to: '/withdraw', icon: ArrowUpRight, label: 'Withdraw' },
    ];

    // Add admin panel link for admin users
    if (user?.is_admin) {
        navItems.push({ to: '/console', icon: Shield, label: 'Admin Console' });
    }

    return (
        <aside className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="sidebar">
            {/* Logo Section */}
            <div className="sidebar-logo">
                <div className="flex items-center gap-3">
                    {/* Logo with glow */}
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-[#5BA4A4] rounded-full blur-md opacity-40 group-hover:opacity-60 transition-opacity" />
                        <img 
                            src={LOGO_URL} 
                            alt="The Cybernetics" 
                            className="relative w-10 h-10 rounded-full border-2 border-[rgba(91,164,164,0.5)]"
                        />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-[#5BA4A4] uppercase tracking-wider font-rajdhani">
                            The Cybernetics
                        </h2>
                        <div className="flex items-center gap-1 mt-0.5">
                            <Zap size={10} className="text-green-400" />
                            <span className="text-[9px] text-green-400 uppercase tracking-wider">Connected</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="sidebar-nav">
                {navItems.map((item, index) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={handleNavClick}
                        className={({ isActive }) => 
                            `nav-item ${isActive ? 'active' : ''}`
                        }
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        style={{ animationDelay: `${index * 0.05}s` }}
                    >
                        <item.icon size={18} strokeWidth={1.5} />
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* User Profile Section */}
            <div className="mt-auto border-t border-[rgba(91,164,164,0.2)]">
                <div className="p-3">
                    {/* Profile Header */}
                    <div className="flex items-center gap-3 p-2">
                        {/* Avatar with Upload */}
                        <div className="relative group">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-[#5BA4A4]/20 border-2 border-[rgba(91,164,164,0.3)] flex items-center justify-center group-hover:border-[rgba(91,164,164,0.5)] transition-colors">
                                {user?.avatar_url ? (
                                    <img 
                                        src={user.avatar_url} 
                                        alt={user.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <User size={18} className="text-[#5BA4A4]" />
                                )}
                            </div>
                            {/* Upload overlay */}
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!uploadingAvatar) fileInputRef.current?.click();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !uploadingAvatar) fileInputRef.current?.click();
                                }}
                                className={`absolute inset-0 flex items-center justify-center bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${uploadingAvatar ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                {uploadingAvatar ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <Camera size={14} className="text-white" />
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleAvatarUpload}
                                className="hidden"
                            />
                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0 text-left">
                            <p className="text-sm font-medium text-white truncate font-rajdhani">
                                {user?.name || 'User'}
                            </p>
                            <p className="text-[10px] text-gray-500 truncate font-mono">
                                {user?.email || ''}
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons - Always Visible */}
                    <div className="mt-2 space-y-1">
                        <NavLink
                            to="/profile"
                            onClick={() => { handleNavClick(); }}
                            className="flex items-center gap-3 px-3 py-2.5 text-gray-400 hover:text-white hover:bg-[rgba(91,164,164,0.1)] rounded-lg transition-all duration-200"
                            data-testid="profile-settings-link"
                        >
                            <Settings size={16} strokeWidth={1.5} />
                            <span className="font-rajdhani uppercase tracking-wider text-xs">Profile Settings</span>
                        </NavLink>
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-all duration-200"
                            data-testid="logout-btn"
                        >
                            <LogOut size={16} strokeWidth={1.5} />
                            <span className="font-rajdhani uppercase tracking-wider text-xs">Logout</span>
                        </button>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
